import { useEffect } from 'react'

import { UseToastOptions } from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import { AgoraRTCErrorCode, AgoraRTCReactError } from 'agora-rtc-react'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import { AgoraLogContext, logAgoraEvent } from 'src/lib/datadog/agoraLogger'

// enum型だと扱いにくいのでunion型で定義
type ErrorCodeUnion = `${AgoraRTCErrorCode}`

const getRTCErrorCode = (error: AgoraRTCReactError): ErrorCodeUnion | null => {
  const rtcError = error?.rtcError
  if (!rtcError) return null
  if (typeof rtcError === 'string') {
    return null
  }
  return rtcError.code
}

const defaultToastOption = (
  media: 'audio' | 'video',
  code?: ErrorCodeUnion,
): UseToastOptions => {
  const mediaText = media === 'audio' ? 'マイク' : 'カメラ'
  return {
    status: 'warning',
    title: `${mediaText}のアクセスに失敗しました。`,
    description: `エラーコード: ${code ?? '不明'}`,
  }
}

const getMediaAccessErrorToastMessage = (
  media: 'audio' | 'video',
  error: AgoraRTCReactError,
): UseToastOptions => {
  const mediaText = media === 'audio' ? 'マイク' : 'カメラ'
  const code = getRTCErrorCode(error)

  if (!code) {
    return defaultToastOption(media)
  }

  // TODO: 必要に応じて他のcodeもハンドリングする（頻発するのは"PERMISSION_DENIED"）
  switch (code) {
    case 'NOT_SUPPORTED':
      return {
        status: 'warning',
        title: 'このブラウザではご利用いただけません。',
        description: '推奨環境をご確認ください。',
      }
    case 'PERMISSION_DENIED':
      return {
        status: 'warning',
        title: `${mediaText}のアクセスが拒否されました。ブラウザの許可設定をご確認ください。`,
        description: '反映されない場合はブラウザの再起動をお試しください。',
      }
    default:
      return defaultToastOption(media, code)
  }
}

const notifySentry = (error: AgoraRTCReactError) => {
  Sentry.captureException(error, {
    level: 'error',
    tags: {
      type: 'VideoCallMediaError',
    },
  })
}

type UseMediaErrorProps = {
  localMicrophoneError: AgoraRTCReactError | null
  localCameraError: AgoraRTCReactError | null
  logContext: AgoraLogContext
}

/** カメラ/マイクのアクセスで問題が発生した場合にtoastを表示する */
export const useMediaError = ({
  localMicrophoneError,
  localCameraError,
  logContext,
}: UseMediaErrorProps) => {
  const toast = useMirohaToast()

  useEffect(() => {
    if (!localMicrophoneError) return
    notifySentry(localMicrophoneError)
    toast(getMediaAccessErrorToastMessage('audio', localMicrophoneError))
    logAgoraEvent('error', 'マイクのアクセスに失敗しました', {
      ...logContext,
      type: 'microphone-access-error',
      code: getRTCErrorCode(localMicrophoneError) ?? undefined,
    })
  }, [localMicrophoneError, toast, logContext])

  useEffect(() => {
    if (!localCameraError) return
    notifySentry(localCameraError)
    toast(getMediaAccessErrorToastMessage('video', localCameraError))
    logAgoraEvent('error', 'カメラのアクセスに失敗しました', {
      ...logContext,
      type: 'camera-access-error',
      code: getRTCErrorCode(localCameraError) ?? undefined,
    })
  }, [localCameraError, toast, logContext])
}
